import { useEffect, useRef } from 'react';
import { aboutStats } from '@/mocks/homeContent';

export default function AboutSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const reveals = el.querySelectorAll('.reveal');
            reveals.forEach((r) => r.classList.add('visible'));
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="about" className="relative py-24 md:py-36 px-4 md:px-6 lg:px-12 overflow-hidden bg-background-50">
      {/* Subtle background glows */}
      <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-primary-50/40 dark:bg-primary-500/5 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] rounded-full bg-accent-50/30 dark:bg-accent-500/5 blur-3xl pointer-events-none" />

      <div className="relative max-w-[1200px] mx-auto">
        {/* Badge */}
        <div className="flex justify-start mb-8 reveal">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-background-300/60 text-xs font-medium text-foreground-600 tracking-wide uppercase bg-transparent">
            <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
            About Cybi Solutions
          </span>
        </div>

        {/* Editorial two-column heading */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-20">
          <div className="lg:col-span-7 reveal">
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-[3.4rem] font-bold text-foreground-950 leading-[1.1] tracking-tight">
              We design,
              <br />
              build, and manage
              <br />
              <span className="relative inline-block">
                <span className="relative z-10">technology systems</span>
                <span className="absolute bottom-1 left-0 w-full h-3 md:h-4 bg-accent-500/30 -z-0 rounded-full" />
              </span>
              <br />
              that help businesses
              <br />
              <span className="text-primary-500">scale with confidence.</span>
            </h2>
          </div>

          <div className="lg:col-span-5 flex flex-col justify-end reveal reveal-delay-1">
            <div className="w-12 h-0.5 bg-primary-400/50 mb-6" />
            <p className="text-base text md:text-lg text-foreground-600 leading-relaxed mb-6">
              Cybi Solutions combines enterprise-grade IT operations with modern software engineering. We are not just another service provider — we are the engineering partner that designs, builds, and manages the technology systems modern businesses need to operate efficiently and grow confidently.
            </p>
            <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
              From proactive infrastructure monitoring to custom SaaS platform development, every engagement is approached with a security-first, engineering-driven mindset. Our team of senior engineers and IT specialists brings decades of combined experience across cloud infrastructure, cybersecurity, application development, and business process automation.
            </p>
            <div className="flex items-center gap-4 mt-8">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-background-100 dark:bg-background-200 flex items-center justify-center">
                  <i className="ri-shield-check-line text-xs text-primary-500 w-4 h-4 flex items-center justify-center" />
                </div>
                <span className="text-xs font-medium text-foreground-600 whitespace-nowrap">Enterprise-grade</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-background-100 dark:bg-background-200 flex items-center justify-center">
                  <i className="ri-code-box-line text-xs text-primary-500 w-4 h-4 flex items-center justify-center" />
                </div>
                <span className="text-xs font-medium text-foreground-600 whitespace-nowrap">Engineering-driven</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-background-100 dark:bg-background-200 flex items-center justify-center">
                  <i className="ri-rocket-2-line text-xs text-primary-500 w-4 h-4 flex items-center justify-center" />
                </div>
                <span className="text-xs font-medium text-foreground-600 whitespace-nowrap">Growth-focused</span>
              </div>
            </div>
          </div>
        </div>

        {/* Stat cards - 4 column grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5 reveal reveal-delay-2">
          {aboutStats.map((stat) => (
            <div
              key={stat.label}
              className="group relative p-6 md:p-8 rounded-2xl bg-background-50 border border-background-200/60 hover:border-primary-200/60 dark:hover:border-primary-500/20 transition-all duration-300 cursor-default"
            >
              {/* Top gradient line */}
              <div className="absolute top-0 left-6 right-6 h-0.5 rounded-full bg-gradient-to-r from-primary-400/0 via-primary-400/40 to-primary-400/0 group-hover:via-primary-400/80 transition-all duration-300" />
              <div
                className="text-4xl md:text-5xl font-heading font-bold text-primary-500 tracking-tight mb-1"
                dangerouslySetInnerHTML={{ __html: stat.value }}
              />
              <div className="text-sm font-semibold text-foreground-950 mb-1">{stat.label}</div>
              <div className="text-xs text-foreground-500">{stat.sublabel}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}